import { AnimatePresence, motion } from 'framer-motion';
import type { ThirdParty, SortField, SortOrder, ActionType, Assessment } from '../types';
import { isNewRequest } from '../types';
import { EXPAND_ANIMATION } from '../constants';
import { TableHeader } from './TableHeader';
import { VendorRow } from './VendorRow';
import { ProductRow } from './ProductRow';
import { NewRequestRow } from './NewRequestRow';

interface ExpandableVendorsTableProps {
  vendors: ThirdParty[];
  expandedVendors: Set<string>;
  onToggleVendor: (vendorName: string) => void;
  sortColumn: SortField | null;
  sortDirection: SortOrder;
  onSort: (column: SortField) => void;
  onAction: (action: ActionType, assessment: Assessment) => void;
}

export function ExpandableVendorsTable({
  vendors,
  expandedVendors,
  onToggleVendor,
  sortColumn,
  sortDirection,
  onSort,
  onAction,
}: ExpandableVendorsTableProps) {
  if (vendors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <p className="text-lg font-medium">No third-parties found</p>
        <p className="text-sm mt-1">Try adjusting your search or filter criteria</p>
      </div>
    );
  }

  return (
    <div role="treegrid" aria-label="Third-party vendors" className="flex-1 min-h-0 flex flex-col">
      <TableHeader
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
      />
      <div role="rowgroup" className="flex-1 min-h-0 overflow-auto">
        {vendors.map((vendor) => {
          const isExpanded = expandedVendors.has(vendor.vendor_name);

          return (
            <div key={vendor.vendor_name} role="group">
              <VendorRow
                vendor={vendor}
                isExpanded={isExpanded}
                onToggle={() => onToggleVendor(vendor.vendor_name)}
              />
              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{
                      duration: EXPAND_ANIMATION.duration,
                      ease: EXPAND_ANIMATION.ease,
                    }}
                    style={{ overflow: 'hidden' }}
                  >
                    {vendor.assessments.map((item, idx) => {
                      if (isNewRequest(item)) {
                        return (
                          <NewRequestRow
                            key={item.externally_filled_intake_form_id}
                            request={item}
                            isLast={idx === vendor.assessments.length - 1}
                          />
                        );
                      }
                      return (
                        <ProductRow
                          key={item.id}
                          assessment={item}
                          vendorName={vendor.vendor_name}
                          isLast={idx === vendor.assessments.length - 1}
                          onAction={onAction}
                        />
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
